import {useCallback, useEffect, useRef} from 'react'

/**
 * Called once per observed element per animation frame with that element's
 * latest {@link ResizeObserverEntry}. Use `entry.target` to identify which
 * element resized.
 */
export type ResizeObserverHandler = (entry: ResizeObserverEntry) => void

/**
 * Observe one or many elements with a single shared,
 * `requestAnimationFrame`-coalesced {@link ResizeObserver}.
 *
 * Returns a stable callback ref — attach it to every element you want to track:
 *
 * ```tsx
 * const ref = useResizeObserver(entry => {
 *   const width = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
 *   // entry.target identifies which element this is
 * })
 * return items.map(item => <Row key={item.id} ref={ref} />)
 * ```
 *
 * Why this instead of `new ResizeObserver(...)` in an effect:
 * - **One observer for N elements.** The browser batches every size change in a
 *   frame into a single callback, so you get one JS task / one render instead of
 *   N. Disambiguate elements with `entry.target`.
 * - **rAF-coalesced + de-duped.** A burst of notifications collapses to a single
 *   flush carrying only the latest entry per element. This avoids the
 *   "ResizeObserver loop" reflow that stalls the presentation phase in
 *   Safari/WebKit (see the `no-state-update-in-resize-observer` lint rule).
 * - **Lifecycle-correct.** Observation starts when an element attaches and stops
 *   when it detaches, unmounts, or is swapped — no stale observations.
 * - **Stable handler.** `onResize` is read from a ref, so updating it never tears
 *   down and re-subscribes the observer, and the returned ref keeps a stable
 *   identity across renders.
 *
 * If your handler calls `setState`, bail when the value is unchanged so the
 * coalesced frame can't drive another resize → render → resize loop:
 *
 * ```tsx
 * setWidth(prev => (prev === next ? prev : next))
 * ```
 *
 * Prefer cheaper tools first (see the package README): a CSS container query for
 * pure styling, `matchMedia` / `@github-ui/screen-size` for viewport
 * breakpoints, and `@github-ui/use-container-breakpoint` for width-only state.
 * Reach for this hook when you need arbitrary element geometry in JS.
 */
export function useResizeObserver(onResize: ResizeObserverHandler): (node: Element | null) => (() => void) | undefined {
  // The observer callback runs outside React's effect phase, so the handler
  // can't be a `useEffectEvent` (those may only be called from effects). Keep
  // the latest handler in a ref instead, so updating it never re-subscribes.
  const handlerRef = useRef(onResize)
  useEffect(() => {
    handlerRef.current = onResize
  }, [onResize])

  const observerRef = useRef<ResizeObserver | null>(null)
  const pendingRef = useRef<Map<Element, ResizeObserverEntry>>(new Map())
  const frameRef = useRef(0)

  const getObserver = useCallback((): ResizeObserver => {
    if (observerRef.current) return observerRef.current

    observerRef.current = new ResizeObserver(entries => {
      // Keep only the latest entry per element so a burst within one frame
      // collapses to a single handler call per element.
      for (const entry of entries) {
        pendingRef.current.set(entry.target, entry)
      }

      cancelAnimationFrame(frameRef.current)
      frameRef.current = requestAnimationFrame(() => {
        const pending = pendingRef.current
        pendingRef.current = new Map()
        for (const entry of pending.values()) {
          handlerRef.current(entry)
        }
      })
    })
    return observerRef.current
  }, [])

  // Disconnect the shared observer and cancel any pending frame on unmount.
  useEffect(() => {
    return () => {
      cancelAnimationFrame(frameRef.current)
      observerRef.current?.disconnect()
      observerRef.current = null
      pendingRef.current = new Map()
    }
  }, [])

  return useCallback(
    (node: Element | null) => {
      if (!node) return
      const observer = getObserver()
      observer.observe(node)
      return () => {
        observer.unobserve(node)
        pendingRef.current.delete(node)
      }
    },
    [getObserver],
  )
}
